// ===== Production Message Input Component =====

import React, { memo, useState, useRef } from 'react';
import { 
  Box, 
  IconButton, 
  TextField, 
  Popover, 
  Grid,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  Send,
  AttachFile,
  EmojiEmotions,
  Image,
  Mic,
  MicOff
} from '@mui/icons-material';
import { CONFIG } from '../config/ChatConfig';
import { validateFile } from '../utils/ValidationUtils';

const MessageInput = ({
  message,
  onMessageChange,
  onSendMessage,
  onKeyPress,
  showEmojiPicker,
  onToggleEmojiPicker,
  disabled,
  hasDataChannel,
  maxLength = CONFIG.MESSAGE.MAX_LENGTH,
  onSendFileMessage, // New prop for sending file messages
  onSendVoiceMessage // New prop for sending voice messages
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const emojiAnchorRef = useRef(null);
  const mediaRecorder = useRef(null);
  const recordingStream = useRef(null);

  const emojis = CONFIG.UI.EMOJI_PICKER_EMOJIS;

  const handleEmojiClick = (emoji) => {
    const newMessage = message + emoji;
    if (newMessage.length <= maxLength) {
      onMessageChange(newMessage);
    }
    onToggleEmojiPicker();
  };

  const handleFileUpload = async (file, type = 'file') => {
    const validation = validateFile(file);
    if (!validation.valid) {
      console.error('File validation failed:', validation.error);
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Convert file to base64 for immediate sending (for demo purposes)
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const fileMessage = {
          text: message.trim() || `Shared ${type}`,
          type: type,
          fileName: validation.sanitizedName,
          url: e.target.result, // base64 data URL
          fileSize: file.size,
          mimeType: file.type
        };

        // Send the file message through the parent component
        if (onSendFileMessage) {
          onSendFileMessage(fileMessage);
          onMessageChange(''); // Clear input after sending
        }
        
        setIsUploading(false);
        setUploadProgress(100);
        
        // Reset progress after a short delay
        setTimeout(() => setUploadProgress(0), 1000);
      };

      reader.onerror = () => {
        console.error('File read error');
        setIsUploading(false);
        setUploadProgress(0);
      };

      // Simulate progress
      let progress = 0;
      const progressInterval = setInterval(() => {
        progress += 20;
        setUploadProgress(progress);
        if (progress >= 80) {
          clearInterval(progressInterval);
        }
      }, 100);

      reader.readAsDataURL(file);

    } catch (error) {
      console.error('File upload error:', error);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileUpload(file, 'image');
    }
    event.target.value = ''; // Reset input
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileUpload(file, 'file');
    }
    event.target.value = ''; // Reset input
  };

  const startVoiceRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: true
      });
      
      recordingStream.current = stream;
      const chunks = [];
      
      mediaRecorder.current = new MediaRecorder(stream);
      
      mediaRecorder.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      
      mediaRecorder.current.onstop = async () => {
        const audioBlob = new Blob(chunks, { type: 'audio/wav' });
        
        // Convert to base64 for immediate sending
        const reader = new FileReader();
        reader.onload = (e) => {
          const audioMessage = {
            text: 'Voice message',
            type: 'audio',
            fileName: 'voice-message.wav',
            url: e.target.result, // base64 data URL
            fileSize: audioBlob.size,
            mimeType: 'audio/wav'
          };

          if (onSendFileMessage) {
            onSendFileMessage(audioMessage);
          }
        };
        
        reader.readAsDataURL(audioBlob);
        
        // Clean up
        recordingStream.current?.getTracks().forEach(track => track.stop());
        recordingStream.current = null;
      };
      
      mediaRecorder.current.start();
      setIsRecording(true);
      
    } catch (error) {
      console.error('Voice recording error:', error);
      alert('Microphone access denied. Please allow microphone access to record voice messages.');
    }
  };

  const stopVoiceRecording = () => {
    if (mediaRecorder.current && isRecording) {
      mediaRecorder.current.stop();
      setIsRecording(false);
    }
  };

  const getCharacterCount = () => {
    return `${message.length}/${maxLength}`;
  };

  const isNearLimit = message.length > maxLength * 0.8;
  const isOverLimit = message.length > maxLength;

  return (
    <Box>
      {/* Upload Progress */}
      {isUploading && (
        <Box style={{ padding: '0 16px' }}>
          <LinearProgress 
            variant="determinate" 
            value={uploadProgress}
            style={{ marginBottom: '8px' }}
          />
        </Box>
      )}

      {/* Main Input Area */}
      <Box
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          padding: '12px 16px',
          backgroundColor: '#f8f9fa',
          borderTop: '1px solid #e0e0e0',
          gap: '10px',
          margin: 0
        }}
      >
        {/* File Attachment Button */}
        <Tooltip title="Attach file">
          <IconButton 
            style={{ color: '#666' }}
            disabled={disabled || isUploading}
            onClick={() => fileInputRef.current?.click()}
          >
            <AttachFile />
          </IconButton>
        </Tooltip>

        {/* Image Attachment Button */}
        <Tooltip title="Send image">
          <IconButton 
            style={{ color: '#666' }}
            disabled={disabled || isUploading}
            onClick={() => imageInputRef.current?.click()}
          >
            <Image />
          </IconButton>
        </Tooltip>

        {/* Message Input Field */}
        <TextField
          multiline
          maxRows={4}
          variant="outlined"
          placeholder={
            hasDataChannel 
              ? "Type a message (WebRTC Data Channel)..." 
              : "Type a message..."
          }
          value={message}
          onChange={(e) => {
            if (e.target.value.length <= maxLength) {
              onMessageChange(e.target.value);
            }
          }}
          onKeyPress={onKeyPress}
          disabled={disabled}
          style={{ flex: 1 }}
          InputProps={{
            style: {
              borderRadius: '20px',
              backgroundColor: '#f5f5f5',
              fontSize: '14px'
            }
          }}
          helperText={
            isNearLimit ? (
              <span style={{ color: isOverLimit ? '#f44336' : '#ff9800' }}>
                {getCharacterCount()}
              </span>
            ) : null
          }
        />

        {/* Voice Recording Button */}
        <Tooltip title={isRecording ? "Stop recording" : "Voice message"}>
          <IconButton
            style={{ 
              color: isRecording ? '#f44336' : '#666',
              backgroundColor: isRecording ? 'rgba(244, 67, 54, 0.1)' : 'transparent'
            }}
            disabled={disabled || isUploading}
            onMouseDown={startVoiceRecording}
            onMouseUp={stopVoiceRecording}
            onMouseLeave={stopVoiceRecording}
          >
            {isRecording ? <MicOff /> : <Mic />}
          </IconButton>
        </Tooltip>

        {/* Emoji Picker Button */}
        <Tooltip title="Add emoji">
          <IconButton 
            ref={emojiAnchorRef}
            style={{ color: '#666' }}
            onClick={onToggleEmojiPicker}
            disabled={disabled}
          >
            <EmojiEmotions />
          </IconButton>
        </Tooltip>

        {/* Send Button */}
        <Tooltip title={
          hasDataChannel 
            ? "Send via WebRTC Data Channel" 
            : "Send via Socket"
        }>
          <IconButton 
            onClick={onSendMessage}
            disabled={disabled || !message.trim() || isOverLimit || isUploading}
            style={{
              backgroundColor: hasDataChannel ? '#25D366' : '#128C7E',
              color: 'white',
              opacity: (disabled || !message.trim() || isOverLimit) ? 0.5 : 1,
              borderRadius: '50%',
              minWidth: '40px',
              height: '40px'
            }}
          >
            <Send />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Emoji Picker Popover */}
      <Popover
        open={showEmojiPicker}
        anchorEl={emojiAnchorRef.current}
        onClose={onToggleEmojiPicker}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <Box style={{ padding: '12px', maxWidth: '280px' }}>
          <Grid container spacing={1}>
            {emojis.map((emoji, index) => (
              <Grid item key={index}>
                <IconButton
                  onClick={() => handleEmojiClick(emoji)}
                  style={{
                    fontSize: '20px',
                    padding: '4px',
                    minWidth: '36px',
                    height: '36px'
                  }}
                >
                  {emoji}
                </IconButton>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Popover>

      {/* Hidden File Inputs */}
      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept={CONFIG.SECURITY.ALLOWED_FILE_TYPES.join(',')}
        onChange={handleFileSelect}
      />
      
      <input
        ref={imageInputRef}
        type="file"
        hidden
        accept={CONFIG.SECURITY.ALLOWED_IMAGE_TYPES.join(',')}
        onChange={handleImageSelect}
      />
    </Box>
  );
};

export default memo(MessageInput);
